import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { List } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'
import { Container, Grid, Box } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import Link from 'gui/components/link'
import MuiSelectWrapper from 'gui/components/common/MuiSelect'
import MenuItem from '@mui/material/MenuItem'
import Accordion from 'gui/components/accordion'
import {
  getDataList as getDataListAction,
  setDataApplicationByKey as setDataApplicationByKeyAction,
} from 'services/data/redux/actions'
import {
  CONTENT_DETAIL_APPLICATION_KEY,
  CONTENT_ROOTS,
  CONTENT_STORE_KEY,
  createContentReferencesToOptions,
} from 'services/content'
import { SORT_TYPE_SEASON_EPISODE } from 'services/content/sort-types'
import { CONTENT_REFERENCE_TYPE_SERIES } from 'services/content-references/types'
import { getViewProcessing, getViewRecordsAsList } from 'services/data'
import { selectData } from 'services/data/redux/selectors'
import { selectLocation } from 'services/location/redux/selectors'
import { getCurrentContentDetail } from './helpers'
import { ContentDetailLoader } from './content-detail-loader'

import './index.scss'

const ALL_VALUE = 'All Seasons'

/**
 * Content Episodes Component
 * @param {Object} props
 * @param {Boolean} props.disabled
 * @returns {import('react').ReactElement}
 */
const ContentEpisodes = () => {
  const [show, toggleShow] = useState(true)
  const [processing, setProcessing] = useState(true)
  const [seasonOptions, setSeasonOptions] = useState([])
  const [seasonNumber, setSeasonNumber] = useState(ALL_VALUE)
  const [episodes, setEpisodes] = useState(List())

  const data = useSelector(selectData)
  const location = useSelector(selectLocation)
  const dispatch = useDispatch()

  const contentDetailProps = {
    data,
    location,
    storeKey: CONTENT_STORE_KEY,
    applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
  }

  useEffect(() => {
    const content = getCurrentContentDetail(contentDetailProps)
    const currentSeasonNumber = content.get('seasonNumber')
    const currentSeasonNumbers = content.get('seasonNumbers')

    const episodeOptions = createContentReferencesToOptions({
      content,
      sort: SORT_TYPE_SEASON_EPISODE,
      type: CONTENT_REFERENCE_TYPE_SERIES,
      seasonNumber: currentSeasonNumber,
    })
    const currentEpisodes = getViewRecordsAsList({
      data,
      storeKey: CONTENT_STORE_KEY,
      options: episodeOptions,
    })
    const isProcessing = getViewProcessing({
      data,
      storeKey: CONTENT_STORE_KEY,
      options: episodeOptions,
    })

    if (currentSeasonNumbers && !seasonOptions.length) {
      setSeasonOptions([ALL_VALUE, ...currentSeasonNumbers])
    }

    setSeasonNumber(currentSeasonNumber || ALL_VALUE)
    setEpisodes(currentEpisodes)
    setProcessing(isProcessing)
  }, [data, location])

  const handleSeasonSelect = (e) => {
    const {
      target: { name, value: targetValue },
    } = e

    let content = getCurrentContentDetail(contentDetailProps)

    // to reset filtration by season number if All Season is checked
    const value = targetValue === ALL_VALUE ? undefined : targetValue
    content = content.set(name, value)

    const episodeOptions = createContentReferencesToOptions({
      content,
      sort: SORT_TYPE_SEASON_EPISODE,
      type: CONTENT_REFERENCE_TYPE_SERIES,
      seasonNumber: value,
    })

    dispatch(
      setDataApplicationByKeyAction({
        storeKey: CONTENT_STORE_KEY,
        applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
        applicationData: content,
      }),
    )

    dispatch(
      getDataListAction({
        storeKey: CONTENT_STORE_KEY,
        options: episodeOptions,
      }),
    )
  }

  return (
    <Container className="content-detail__section publishing" maxWidth={false}>
      <Accordion title="Episodes" toggleShow={() => toggleShow(!show)} show={show} />
      {show && (
        <>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={2}>
              <MuiSelectWrapper
                name="seasonNumber"
                className="content-detail__input"
                value={seasonNumber || ALL_VALUE}
                onChange={(e) => {
                  handleSeasonSelect(e)
                }}
              >
                {seasonOptions.map((season) => (
                  <MenuItem value={season} key={`season-${season}`}>
                    {season}
                  </MenuItem>
                ))}
              </MuiSelectWrapper>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Grid
              container
              spacing={1}
              sx={{
                borderBottom: '1px solid #e0e0e0',
                pb: 1,
                mb: 1,
                fontWeight: 'bold',
              }}
            >
              <Grid item xs={1}>
                Season
              </Grid>
              <Grid item xs={1}>
                Episode
              </Grid>
              <Grid item xs={8}>
                Title
              </Grid>
              <Grid item xs={1}>
                Language
              </Grid>
              <Grid item xs={1}>
                Published
              </Grid>
            </Grid>
            {processing ? (
              <ContentDetailLoader />
            ) : (
              episodes.map((item) => (
                <Grid
                  container
                  spacing={1}
                  key={`episode-list-row-${item.get('id')}`}
                  sx={{
                    py: 1,
                    borderBottom: '1px solid #f5f5f5',
                    '&:hover': { backgroundColor: '#f9f9f9' },
                  }}
                >
                  <Grid item xs={1}>
                    {item.get('seasonNumber')}
                  </Grid>
                  <Grid item xs={1}>
                    {item.get('episodeNumber')}
                  </Grid>
                  <Grid item xs={8}>
                    <Link href={`${CONTENT_ROOTS.V2}/${item.get('id')}`}>{item.get('title')}</Link>
                  </Grid>
                  <Grid item xs={1}>
                    {item.get('language')}
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    {item.get('published') ? (
                      <CheckIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                    ) : (
                      <CloseIcon sx={{ color: '#c62828', fontSize: 20 }} />
                    )}
                  </Grid>
                </Grid>
              ))
            )}
          </Box>
        </>
      )}
    </Container>
  )
}

ContentEpisodes.propTypes = {
  disabled: PropTypes.bool,
}

export default ContentEpisodes
